import type { ActivitySource } from "@/shared/api/activity-source";
import {
  browserTimeZone,
  calendarCapabilities,
  fetchCalendarStatus,
  hasGoogleTasksWriteScope,
  syncGoogleTasks,
  type CalendarStatus,
} from "@/shared/api/calendar";
import {
  captureAuthSessionOwner,
  isAuthSessionOwnerCurrent,
  type AuthSessionOwner,
} from "@/shared/auth/session";
import {
  pendingCurrentContentPushOptions,
  type ContentPushIntentReceipt,
} from "@/shared/sync/content-push-intent";
import {
  captureGoogleIntegrationsRuntimeGeneration,
  isGoogleIntegrationsRuntimeGenerationCurrent,
  onGoogleIntegrationsRuntimeBoundary,
} from "@/shared/google/google-integration-runtime.mjs";
import {
  pullAccountSyncAfterProvider,
  pushTasksForConnectedProvider,
} from "@/shared/sync/cloud-sync";
import { tasksToSyncPayload } from "@/shared/tasks/store";

export type TasksProviderSyncResult =
  | "account-sync-failed"
  | "account-synced"
  | "google-sync-pending"
  | "google-synced";

const GOOGLE_TASKS_RECONCILE_REQUEST_EVENT = "tb-public-web-google-tasks-reconcile-request";

export function requestGoogleTasksInboundReconcile(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(GOOGLE_TASKS_RECONCILE_REQUEST_EVENT));
}

export function onGoogleTasksInboundReconcileRequested(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(GOOGLE_TASKS_RECONCILE_REQUEST_EVENT, listener);
  return () => window.removeEventListener(GOOGLE_TASKS_RECONCILE_REQUEST_EVENT, listener);
}

function pendingOutboundGoogleSync(): TasksProviderSyncResult {
  requestGoogleTasksInboundReconcile();
  return "google-sync-pending";
}

function writebackConflictCount(result: unknown): number {
  const count = Number((result as { writebackConflicts?: unknown } | null)?.writebackConflicts);
  return Number.isSafeInteger(count) && count > 0 ? count : 0;
}

type TasksProviderSyncDependencies = {
  pushAccountTasks?: (opts: { expectedOwner: AuthSessionOwner }) => Promise<unknown>;
  fetchStatus?: (opts?: { activitySource?: ActivitySource }) => Promise<CalendarStatus>;
  syncGoogle?: (
    timeZone: string,
    opts?: { signal?: AbortSignal; activitySource?: ActivitySource },
  ) => Promise<unknown>;
  pullAccountTasks?: (opts: {
    expectedOwner: AuthSessionOwner;
    assertCanApply: () => void;
  }) => Promise<unknown>;
  captureOwner?: () => AuthSessionOwner | null;
  isOwnerCurrent?: (owner: AuthSessionOwner) => boolean;
  taskFingerprint?: () => string;
  hasPendingLocalTaskPush?: () => boolean;
  readPendingTaskPushIntent?: () => ContentPushIntentReceipt;
  acknowledgePendingTaskPushIntent?: (receipt: ContentPushIntentReceipt) => void;
  captureGoogleRuntimeGeneration?: () => number | null;
  isGoogleRuntimeGenerationCurrent?: (generation: number) => boolean;
  onGoogleRuntimeBoundary?: (listener: () => void) => () => void;
  googleTimeoutMs?: number;
};

export type TasksProviderSyncInput = {
  activitySource?: ActivitySource;
  connection?: CalendarStatus | null;
  googleIntegrationsEnabled?: boolean;
  timeZone?: string;
};

function sameTasksConnectionGeneration(
  left: CalendarStatus | null | undefined,
  right: CalendarStatus | null | undefined,
): boolean {
  const leftCapabilities = calendarCapabilities(left);
  const rightCapabilities = calendarCapabilities(right);
  return Boolean(
    left?.connected === true &&
    right?.connected === true &&
    left.accountId &&
    left.accountId === right.accountId &&
    typeof left.connectionVersion === "number" &&
    left.connectionVersion === right.connectionVersion &&
    leftCapabilities.calendarEventsWritable === rightCapabilities.calendarEventsWritable &&
    leftCapabilities.tasksWritable &&
    rightCapabilities.tasksWritable,
  );
}

/**
 * Reconcile Google into the account mirror without exporting the browser's
 * current task snapshot first. This is the only safe order for foreground and
 * explicit refreshes: a stale local open row must not overwrite a completion
 * the user just made in Google. Explicit local edits continue through the
 * push-first coordinator below.
 */
export async function pullGoogleTasksFromConnectedProvider(
  input: TasksProviderSyncInput = {},
  deps: TasksProviderSyncDependencies = {},
): Promise<TasksProviderSyncResult> {
  const activitySource = input.activitySource;
  const fetchStatus = deps.fetchStatus || fetchCalendarStatus;
  const syncGoogle = deps.syncGoogle || syncGoogleTasks;
  const pullAccountTasks = deps.pullAccountTasks || pullAccountSyncAfterProvider;
  const captureOwner = deps.captureOwner || captureAuthSessionOwner;
  const isOwnerCurrent = deps.isOwnerCurrent || isAuthSessionOwnerCurrent;
  const taskFingerprint = deps.taskFingerprint || (() => JSON.stringify(tasksToSyncPayload()));
  const hasPendingLocalTaskPush =
    deps.hasPendingLocalTaskPush ||
    (() => pendingCurrentContentPushOptions().includeTasks === true);
  const captureGoogleRuntimeGeneration =
    deps.captureGoogleRuntimeGeneration || captureGoogleIntegrationsRuntimeGeneration;
  const isGoogleRuntimeGenerationCurrent =
    deps.isGoogleRuntimeGenerationCurrent || isGoogleIntegrationsRuntimeGenerationCurrent;
  const onGoogleRuntimeBoundary =
    deps.onGoogleRuntimeBoundary || onGoogleIntegrationsRuntimeBoundary;
  const expectedOwner = captureOwner();
  if (!expectedOwner) return "account-sync-failed";
  if (input.googleIntegrationsEnabled !== true) return "account-synced";
  const googleRuntimeGeneration = captureGoogleRuntimeGeneration();
  if (googleRuntimeGeneration === null) return "account-synced";
  const googleRuntimeCurrent = () => isGoogleRuntimeGenerationCurrent(googleRuntimeGeneration);
  const pendingOrAccountSynced = () =>
    googleRuntimeCurrent() && isOwnerCurrent(expectedOwner)
      ? pendingOutboundGoogleSync()
      : "account-synced";
  if (!googleRuntimeCurrent() || !isOwnerCurrent(expectedOwner)) return "account-synced";
  if (hasPendingLocalTaskPush()) return "google-sync-pending";

  let connection: CalendarStatus;
  try {
    connection = await fetchStatus(activitySource ? { activitySource } : undefined);
  } catch {
    return pendingOrAccountSynced();
  }
  if (!googleRuntimeCurrent() || !isOwnerCurrent(expectedOwner)) return "account-synced";
  if (hasPendingLocalTaskPush()) return "google-sync-pending";
  if (
    !hasGoogleTasksWriteScope(connection) ||
    (input.connection && !sameTasksConnectionGeneration(input.connection, connection))
  )
    return "account-synced";
  const expectedTaskFingerprint = taskFingerprint();
  const syncController = new AbortController();
  const syncDeadline = globalThis.setTimeout(
    () => syncController.abort(),
    Math.max(1_000, deps.googleTimeoutMs || 30_000),
  );
  const stopRuntimeBoundary = onGoogleRuntimeBoundary(() => {
    if (!googleRuntimeCurrent()) syncController.abort();
  });
  let googleSyncResult: unknown;
  try {
    if (!googleRuntimeCurrent() || !isOwnerCurrent(expectedOwner)) return "account-synced";
    googleSyncResult = await syncGoogle(input.timeZone || browserTimeZone(), {
      signal: syncController.signal,
      ...(activitySource ? { activitySource } : {}),
    });
  } catch {
    return pendingOrAccountSynced();
  } finally {
    globalThis.clearTimeout(syncDeadline);
    stopRuntimeBoundary();
  }

  if (!googleRuntimeCurrent() || !isOwnerCurrent(expectedOwner)) return "account-synced";
  if (hasPendingLocalTaskPush() || taskFingerprint() !== expectedTaskFingerprint)
    return "google-sync-pending";

  try {
    if (!googleRuntimeCurrent() || !isOwnerCurrent(expectedOwner)) return "account-synced";
    await pullAccountTasks({
      expectedOwner,
      assertCanApply: () => {
        if (
          !googleRuntimeCurrent() ||
          !isOwnerCurrent(expectedOwner) ||
          hasPendingLocalTaskPush() ||
          taskFingerprint() !== expectedTaskFingerprint
        ) {
          throw new Error("tasks_provider_pull_superseded");
        }
      },
    });
    if (!googleRuntimeCurrent() || !isOwnerCurrent(expectedOwner)) return "account-synced";
    if (writebackConflictCount(googleSyncResult) > 0) return "google-sync-pending";
    return "google-synced";
  } catch {
    return pendingOrAccountSynced();
  }
}

/**
 * Persist the local task mirror first, then fan it out to Google only when the
 * provider-confirmed Tasks write scope authorizes two-way synchronization.
 * Google is intentionally best-effort: a transient provider failure must not
 * roll back a local task mutation or misreport the account sync as failed.
 */
export async function pushTasksToConnectedProviders(
  input: TasksProviderSyncInput = {},
  deps: TasksProviderSyncDependencies = {},
): Promise<TasksProviderSyncResult> {
  const activitySource = input.activitySource;
  const pushAccountTasks = deps.pushAccountTasks || pushTasksForConnectedProvider;
  const fetchStatus = deps.fetchStatus || fetchCalendarStatus;
  const syncGoogle = deps.syncGoogle || syncGoogleTasks;
  const pullAccountTasks = deps.pullAccountTasks || pullAccountSyncAfterProvider;
  const captureOwner = deps.captureOwner || captureAuthSessionOwner;
  const isOwnerCurrent = deps.isOwnerCurrent || isAuthSessionOwnerCurrent;
  const taskFingerprint = deps.taskFingerprint || (() => JSON.stringify(tasksToSyncPayload()));
  const captureGoogleRuntimeGeneration =
    deps.captureGoogleRuntimeGeneration || captureGoogleIntegrationsRuntimeGeneration;
  const isGoogleRuntimeGenerationCurrent =
    deps.isGoogleRuntimeGenerationCurrent || isGoogleIntegrationsRuntimeGenerationCurrent;
  const onGoogleRuntimeBoundary =
    deps.onGoogleRuntimeBoundary || onGoogleIntegrationsRuntimeBoundary;
  const expectedOwner = captureOwner();
  const pushedTaskFingerprint = taskFingerprint();
  if (!expectedOwner) return "account-sync-failed";
  const googleRuntimeGeneration =
    input.googleIntegrationsEnabled === true ? captureGoogleRuntimeGeneration() : null;
  const googleRuntimeCurrent = () =>
    googleRuntimeGeneration !== null && isGoogleRuntimeGenerationCurrent(googleRuntimeGeneration);
  const pendingOrAccountSynced = () =>
    googleRuntimeCurrent() && isOwnerCurrent(expectedOwner)
      ? pendingOutboundGoogleSync()
      : "account-synced";
  const pendingTaskPushIntent = deps.readPendingTaskPushIntent?.() || null;

  let pushResult: unknown;
  try {
    pushResult = await pushAccountTasks({ expectedOwner });
  } catch {
    return "account-sync-failed";
  }
  // Account push is owner-fenced internally, but the active browser session
  // can still change immediately after it resolves. Never let a request that
  // began for A borrow B's bearer token for destructive provider sync.
  if (!isOwnerCurrent(expectedOwner)) return "account-synced";
  if (taskFingerprint() !== pushedTaskFingerprint) return pendingOrAccountSynced();
  let taskIntentAcknowledgementFailed =
    Boolean(pushResult) &&
    typeof pushResult === "object" &&
    (pushResult as { taskIntentAcknowledged?: unknown }).taskIntentAcknowledged === false;
  if (pendingTaskPushIntent && deps.acknowledgePendingTaskPushIntent) {
    try {
      deps.acknowledgePendingTaskPushIntent(pendingTaskPushIntent);
    } catch {
      taskIntentAcknowledgementFailed = true;
    }
  }

  // The account mirror remains authoritative while the public Google switch
  // is OFF. Stop here before status, OAuth-derived capability, or provider API
  // access; local/account task persistence still completed above.
  if (input.googleIntegrationsEnabled !== true || !googleRuntimeCurrent()) {
    return "account-synced";
  }

  let connection = input.connection;
  if (!connection) {
    try {
      connection = await fetchStatus(activitySource ? { activitySource } : undefined);
    } catch {
      return pendingOrAccountSynced();
    }
  }
  if (!googleRuntimeCurrent()) return "account-synced";
  if (!isOwnerCurrent(expectedOwner)) return "account-synced";
  if (taskFingerprint() !== pushedTaskFingerprint) return pendingOrAccountSynced();
  if (!hasGoogleTasksWriteScope(connection)) return "account-synced";

  const syncController = new AbortController();
  const syncDeadline = globalThis.setTimeout(
    () => syncController.abort(),
    Math.max(1_000, deps.googleTimeoutMs || 30_000),
  );
  const stopRuntimeBoundary = onGoogleRuntimeBoundary(() => {
    if (!googleRuntimeCurrent()) syncController.abort();
  });
  let googleSyncResult: unknown;
  try {
    if (!googleRuntimeCurrent() || !isOwnerCurrent(expectedOwner)) return "account-synced";
    googleSyncResult = await syncGoogle(input.timeZone || browserTimeZone(), {
      signal: syncController.signal,
      ...(activitySource ? { activitySource } : {}),
    });
  } catch {
    return pendingOrAccountSynced();
  } finally {
    globalThis.clearTimeout(syncDeadline);
    stopRuntimeBoundary();
  }

  // Google Tasks sync adopts remote creates/deletes into the account mirror.
  // Pull that authoritative mirror back into this browser only if the exact
  // local task generation we pushed still owns the result. A newer edit is
  // queued by the coordinator and must run push -> Google before any pull can
  // apply, so stale provider data never overwrites it.
  if (!googleRuntimeCurrent() || !isOwnerCurrent(expectedOwner)) return "account-synced";
  if (taskFingerprint() !== pushedTaskFingerprint) return pendingOrAccountSynced();
  try {
    if (!googleRuntimeCurrent() || !isOwnerCurrent(expectedOwner)) return "account-synced";
    await pullAccountTasks({
      expectedOwner,
      assertCanApply: () => {
        if (
          !googleRuntimeCurrent() ||
          !isOwnerCurrent(expectedOwner) ||
          taskFingerprint() !== pushedTaskFingerprint
        ) {
          throw new Error("tasks_provider_pull_superseded");
        }
      },
    });
    if (!googleRuntimeCurrent() || !isOwnerCurrent(expectedOwner)) return "account-synced";
    if (taskIntentAcknowledgementFailed || writebackConflictCount(googleSyncResult) > 0) {
      return pendingOutboundGoogleSync();
    }
    return "google-synced";
  } catch {
    return pendingOrAccountSynced();
  }
}

/**
 * A component-scoped latest-wins queue prevents rapid edits from racing two
 * account pushes or creating a Google sync storm. Every caller shares the
 * active drain and receives the result of the newest queued snapshot.
 */
export function createTasksProviderSyncCoordinator(
  run: typeof pushTasksToConnectedProviders = pushTasksToConnectedProviders,
  onSettled?: (result: TasksProviderSyncResult) => void | Promise<unknown>,
) {
  let inFlight: Promise<TasksProviderSyncResult> | null = null;
  let queuedInput: TasksProviderSyncInput | null = null;

  return function scheduleTasksProviderSync(
    input: TasksProviderSyncInput = {},
  ): Promise<TasksProviderSyncResult> {
    queuedInput = input;
    if (inFlight) return inFlight;

    inFlight = (async () => {
      let result: TasksProviderSyncResult = "account-synced";
      while (true) {
        while (queuedInput) {
          const nextInput = queuedInput;
          queuedInput = null;
          result = await run(nextInput);
        }
        try {
          if (
            (result === "google-synced" || result === "google-sync-pending") &&
            captureGoogleIntegrationsRuntimeGeneration() !== null
          ) {
            await onSettled?.(result);
          }
        } catch {
          // Cache refresh is a projection concern. It must not convert an
          // already-settled account/provider sync into a failed task save.
        }
        // A mutation queued while the settled projection was refreshing must
        // be drained by this same serialized worker rather than getting lost
        // behind the still-in-flight promise.
        if (!queuedInput) return result;
      }
    })().finally(() => {
      inFlight = null;
    });
    return inFlight;
  };
}
